import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useTypewriter } from "../hooks/useTypewriter";
import { geminiService } from "../services/geminiService";
import { dbService } from "../services/dbService";
import { Language, CreationRecord, User as UserType } from "../types";
import TypewriterTextarea from "./TypewriterTextarea";
import GenerationProgress from "./GenerationProgress";
import {
  Loader2,
  Send,
  Bot,
  User,
  Zap,
  ArrowRight,
  FileText,
  Cpu,
  Palette,
  Image as ImageIcon,
  Download,
  RefreshCw,
  Wand2,
  PlayCircle,
  AlertCircle,
  Maximize2,
  X,
  CheckCircle2,
  Circle,
  MapPin,
  Coins,
  ShieldCheck,
  ClipboardCheck,
  Package,
  Clock,
  LayoutList,
  LayoutGrid,
  Edit3,
  Check,
  Tag,
  Sparkles,
  FastForward,
  FileDown,
  ChevronLeft,
  ChevronRight,
  PanelsLeftBottom,
  PanelsRightBottom,
  ImagePlay,
  Coffee,
  Watch,
  Music,
  Lamp,
  Smartphone,
  Camera,
  Info,
  Rocket,
  History as HistoryIcon,
  Layers,
  MessageSquareHeart,
  Layers2,
  ChevronDown,
  Wind,
} from "lucide-react";

interface Props {
  onComplete: (prd: string, idea: string, mainImage: string) => void;
  onSelectRecord: (record: CreationRecord) => void;
  lang: Language;
  historyRecords: CreationRecord[];
  projectId: string;
  points: number;
  user: UserType | null;
  onAuthRequired: () => void;
  deductPoints: (
    amount: number,
    description: { zh: string; en: string },
    projectName?: string,
  ) => void;
  onProjectCreated?: (record: CreationRecord) => void;
  refreshHistory?: () => void;
  onNewProject?: () => void;
}

interface Message {
  role: "ai" | "user";
  content: string;
  options?: string[];
  answered?: boolean;
  targetField?: string;
  optimizedPrompt?: string;
}

const INSPIRATION_IDEAS = [
  {
    id: "alba",
    icon: <Coffee className="w-5 h-5" />,
    zh: "Alba Smart Juicer",
    en: "Alba Smart Juicer",
    image: "/img/alba-smart-juicer.png",
    details: {
      description: {
        zh: "一款专为现代厨房设计的智能原汁机，采用简单的触摸控制和柔和的光反馈，让日常榨汁变得轻松愉快。",
        en: "A smart juicer designed for modern kitchens, using simple touch controls and soft light feedback to make everyday juicing easy and enjoyable.",
      },
      targetUsers: {
        zh: "享受健康饮品的年轻专业人士和小型家庭。",
        en: "Young professionals and small families who enjoy healthy drinks.",
      },
      features: {
        zh: ["智能榨汁模式", "极简触摸控制", "灯光状态反馈", "易清洗结构"],
        en: [
          "Smart juicing modes",
          "Minimal touch controls",
          "Light status feedback",
          "Easy-clean structure",
        ],
      },
    },
  },
  {
    id: "aeria",
    icon: <Wind className="w-5 h-5" />,
    zh: "Aeria Air Purifier",
    en: "Aeria Air Purifier",
    image: "/img/aeria-air-purifier.png",
    details: {
      description: {
        zh: "Aeria 是一款结合了空气净化和可定制香氛混合的家用设备。其分隔式香氛托盘允许同时使用多个香氛蜡块，在改善室内空气质量的同时营造个性化香气。",
        en: "Aeria is a home device that combines air purification with customizable fragrance blending. Its divided fragrance tray allows multiple scented wax cubes to be used at the same time, creating a personalized aroma while improving indoor air quality.",
      },
      targetUsers: {
        zh: "重视空气清洁和舒适家庭氛围的城市用户和年轻家庭。",
        en: "Urban users and young families who value clean air and a comfortable home atmosphere.",
      },
      features: {
        zh: ["分隔式香氛托盘", "多重香氛混合", "空气净化系统", "环境光指示"],
        en: [
          "Divided fragrance tray",
          "Multi-scent blending",
          "Air purification system",
          "Ambient light indicator",
        ],
      },
    },
  },
  {
    id: "sora",
    icon: <Sparkles className="w-5 h-5" />,
    zh: "Sora Meditation Diffuser",
    en: "Sora Meditation Diffuser",
    image: "/img/sora-meditation-diffuser.png",
    details: {
      description: {
        zh: "Sora 是一款结合了香氛扩散和冥想的家用设备。灵感源自日本香道中使用的源氏香图案，它将感知香气的行为转化为一种现代的日常仪式。当扩香器启动时，柔和的环境光穿过顶面的几何图案，缓慢律动以引导呼吸，营造出平静、专注的氛围。",
        en: "Sora is a home device that combines aroma diffusion with meditation. Inspired by the Genji-kō patterns used in the Japanese incense tradition, it transforms the act of sensing fragrance into a modern daily ritual. When the diffuser is activated, soft ambient light glows through the geometric pattern on the top surface, slowly pulsing to guide breathing and create a calm, focused atmosphere.",
      },
      targetUsers: {
        zh: "在家中寻求放松、冥想或平静氛围的人。",
        en: "People who seek relaxation, meditation, or a calm atmosphere at home.",
      },
      features: {
        zh: ["香氛扩散系统", "呼吸律动灯", "冥想氛围支持", "环境光指示"],
        en: [
          "Aroma diffusion system",
          "Breathing rhythm light",
          "Meditation atmosphere support",
          "Ambient light indicator",
        ],
      },
    },
  },
  {
    id: "fold",
    icon: <Lamp className="w-5 h-5" />,
    zh: "Fold Time Desk Lamp",
    en: "Fold Time Desk Lamp",
    image: "/img/fold-time-desk-lamp.png",
    details: {
      description: {
        zh: "一款专为现代办公空间设计的极简折叠台灯，将照明、时间显示和无线充电集成到一个可变形的物体中。",
        en: "A minimalist folding desk lamp designed for modern workspaces, integrating lighting, time display, and wireless charging into one transformable object.",
      },
      targetUsers: {
        zh: "重视办公空间整洁高效的上班族和家庭用户。",
        en: "Office workers and home users who value an organized and efficient workspace.",
      },
      features: {
        zh: ["折叠变形设计", "集成时间显示", "无线充电底座", "柔和任务照明"],
        en: [
          "Folding transformable design",
          "Integrated time display",
          "Wireless charging base",
          "Soft task lighting",
        ],
      },
    },
  },
];

const MANDATORY_FIELDS = [
  "projectName",
  "coreDescription",
  "scenariosPainPoints",
  "userPersona",
  "appearanceMaterials",
  "featureDetails",
  "costBom",
];
const DIALOGUE_GUIDED_FIELDS = [
  "scenariosPainPoints",
  "userPersona",
  "appearanceMaterials",
  "featureDetails",
  "costBom",
];

const PRD_HIERARCHY = [
  {
    id: "group_overview",
    label: { zh: "1. 产品概览", en: "1. Product Overview" },
    items: [
      {
        id: "projectName",
        label: { zh: "产品或项目名称", en: "Project Name" },
        icon: <Tag className="w-3 h-3" />,
      },
      {
        id: "coreDescription",
        label: { zh: "产品核心描述", en: "Product Description" },
        icon: <FileText className="w-3 h-3" />,
      },
      {
        id: "scenariosPainPoints",
        label: { zh: "使用场景与核心痛点", en: "Scenarios & Pain Points" },
        icon: <MapPin className="w-3 h-3" />,
      },
      {
        id: "userPersona",
        label: { zh: "目标用户画像", en: "Target User Profile" },
        icon: <User className="w-3 h-3" />,
      },
    ],
  },
  {
    id: "group_detailed",
    label: { zh: "2. 详细规格", en: "2. Detailed Specs" },
    items: [
      {
        id: "appearanceMaterials",
        label: { zh: "外观偏好与材质质感", en: "Design & Materials" },
        icon: <Palette className="w-3 h-3" />,
      },
      {
        id: "featureDetails",
        label: { zh: "核心功能细节", en: "Functionality" },
        icon: <Cpu className="w-3 h-3" />,
      },
      {
        id: "interactionOperation",
        label: { zh: "用户交互与操作", en: "Interaction" },
        icon: <PlayCircle className="w-3 h-3" />,
      },
      {
        id: "keyComponents",
        label: { zh: "关键零部件定义", en: "Hardware" },
        icon: <ShieldCheck className="w-3 h-3" />,
      },
      {
        id: "controlLogic",
        label: { zh: "控制逻辑与软件", en: "Software Logic" },
        icon: <FileText className="w-3 h-3" />,
      },
    ],
  },
  {
    id: "group_production",
    label: { zh: "3. 生产与性能", en: "3. Production & Performance" },
    items: [
      {
        id: "performanceGoals",
        label: { zh: "性能目标与效率指标", en: "Performance" },
        icon: <Zap className="w-3 h-3" />,
      },
      {
        id: "costBom",
        label: { zh: "成本控制与BOM预算", en: "BOM Cost" },
        icon: <Coins className="w-3 h-3" />,
      },
      {
        id: "mfgQuality",
        label: { zh: "制造品质与耐用性", en: "Quality & Durability" },
        icon: <ShieldCheck className="w-3 h-3" />,
      },
      {
        id: "sizePkg",
        label: { zh: "尺寸限制与包装需求", en: "Size & Packaging" },
        icon: <Package className="w-3 h-3" />,
      },
    ],
  },
  {
    id: "group_constraints",
    label: { zh: "4. 约束与进度", en: "4. Constraints" },
    items: [
      {
        id: "supplyChain",
        label: { zh: "供应链与材料约束", en: "Existing Resources" },
        icon: <Coins className="w-3 h-3" />,
      },
      {
        id: "compliance",
        label: { zh: "行业合规标准", en: "Standards" },
        icon: <ClipboardCheck className="w-3 h-3" />,
      },
      {
        id: "deliveryMilestones",
        label: { zh: "交付时间节点", en: "Milestones" },
        icon: <Clock className="w-3 h-3" />,
      },
    ],
  },
];

const FLAT_PRD_STRUCTURE = PRD_HIERARCHY.flatMap((group) => group.items);
const DIALOGUE_GUIDED_STRUCTURE = FLAT_PRD_STRUCTURE.filter((f) =>
  DIALOGUE_GUIDED_FIELDS.includes(f.id),
);
const TOTAL_DIMENSIONS = FLAT_PRD_STRUCTURE.length;
const GENERATION_COST = 4;
const REALITY_COST = 0;

const TYPEWRITER_WORDS = {
  zh: [
    "我想设计一个智能咖啡机",
    "我想设计一个极简折叠台灯",
    "我想设计一个桌面空气净化器",
    "我想设计一个智能温控水杯",
    "我想设计一个便携式筋膜枪",
    "我想设计一个降噪睡眠耳塞",
    "我想设计一个磁吸无线充电宝",
    "我想设计一个智能感应垃圾桶",
  ],
  en: [
    "I want to design a smart coffee machine",
    "I want to design a minimalist folding lamp",
    "I want to design a desktop air purifier",
    "I want to design a smart temperature control mug",
    "I want to design a portable massage gun",
    "I want to design noise-cancelling sleep earbuds",
    "I want to design a magnetic wireless power bank",
    "I want to design a smart sensor trash can",
  ],
};

const RENDER_STATUS_MESSAGES = {
  zh: [
    "正在解析 PRD 核心定义...",
    "正在构建工业设计材质模型...",
    "正在模拟光影环境与渲染...",
    "正在进行 4K 细节增强处理...",
    "正在优化几何拓扑结构...",
    "正在校准色彩与对比度...",
  ],
  en: [
    "Analyzing PRD specs...",
    "Synthesizing material textures...",
    "Simulating lighting environment...",
    "Finalizing 4K industrial render...",
    "Optimizing geometric topology...",
    "Calibrating color and contrast...",
  ],
};

const cleanOptions = (options?: string[]) => {
  if (!options) return undefined;
  return options.map((opt) => opt.replace(/^[0-9]+[:.]\s*/, "").trim());
};

const IdeationStep: React.FC<Props> = ({
  onComplete,
  onSelectRecord,
  lang: initialLang,
  historyRecords,
  projectId,
  points,
  user,
  onAuthRequired,
  deductPoints,
  onProjectCreated,
  refreshHistory,
  onNewProject,
}) => {
  const [lang, setLang] = useState<Language>(initialLang);
  const [initialIdea, setInitialIdea] = useState("");
  const [selectedInspiration, setSelectedInspiration] = useState<
    (typeof INSPIRATION_IDEAS)[0] | null
  >(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStarted, setIsStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prdData, setPrdData] = useState<Record<string, any>>({});
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const [rightPanelTab, setRightPanelTab] = useState<"prd" | "visuals">("prd");
  const [isRightPanelExpanded, setIsRightPanelExpanded] = useState(true);

  const [versionedImages, setVersionedImages] = useState<string[][]>([]);
  const [viewVersionIndex, setViewVersionIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [refinementPrompt, setRefinementPrompt] = useState("");

  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);
  const [designRequirement, setDesignRequirement] = useState("");
  const [requirementInput, setRequirementInput] = useState("");
  const [requirementAnalysisCount, setRequirementAnalysisCount] = useState(0);

  const [customInputValue, setCustomInputValue] = useState("");

  const [isSubmittedForReality, setIsSubmittedForReality] = useState(false);
  const [showRealitySuccessModal, setShowRealitySuccessModal] = useState(false);
  const [showFillMandatoryModal, setShowFillMandatoryModal] = useState(false);
  const [tempMandatoryData, setTempMandatoryData] = useState<
    Record<string, string>
  >({});

  const [showSatisfactionPrompt, setShowSatisfactionPrompt] = useState(false);
  const [failedAction, setFailedAction] = useState<{
    fn: () => void;
    label: string;
  } | null>(null);

  const [projectNo, setProjectNo] = useState<string | null>(null);
  const [backendProjectId, setBackendProjectId] = useState<number | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const [totalAmount, setTotalAmount] = useState(0);

  const handleDeductPoints = (
    amount: number,
    description: { zh: string; en: string },
    projectName?: string,
  ) => {
    deductPoints(amount, description, projectName);
  };

  const getPrdDataForBackend = (currentPrd: any) => {
    return {
      project_name: currentPrd.projectName || "",
      description: currentPrd.coreDescription || "",
      use_scenario_pain: currentPrd.scenariosPainPoints || "",
      user_portrait: currentPrd.userPersona || "",
      appearance_material: currentPrd.appearanceMaterials || "",
      core_function:
        typeof currentPrd.featureDetails === "object"
          ? JSON.stringify(currentPrd.featureDetails)
          : currentPrd.featureDetails || "",
      user_interaction: currentPrd.interactionOperation || "",
      key_component:
        typeof currentPrd.keyComponents === "object"
          ? JSON.stringify(currentPrd.keyComponents)
          : currentPrd.keyComponents || "",
      control_logic: currentPrd.controlLogic || "",
      performance_indicator: currentPrd.performanceGoals || "",
      cost_budget:
        typeof currentPrd.costBom === "object"
          ? JSON.stringify(currentPrd.costBom)
          : currentPrd.costBom || "",
      quality_durability: currentPrd.mfgQuality || "",
      size_packaging: currentPrd.sizePkg || "",
      supply_chain: currentPrd.supplyChain || "",
      compliance: currentPrd.compliance || "",
      delivery_milestones: currentPrd.deliveryMilestones || "",
    };
  };

  const calculateProgress = (data: any) => {
    if (!data) return 0;
    const filledFields = FLAT_PRD_STRUCTURE.filter((field) => {
      const value = data[field.id];
      return value && typeof value === "string" && value.trim().length > 0;
    });
    return Math.round((filledFields.length / TOTAL_DIMENSIONS) * 100);
  };

  const handleSwitchVersion = async (idx: number, isRequired?: boolean) => {
    setViewVersionIndex(idx);
    if (backendProjectId && isRequired) {
      try {
        await dbService.switchProjectImageVersion(backendProjectId, idx + 1);
        if (refreshHistory) refreshHistory();
      } catch (e) {
        console.error("Failed to switch project image version", e);
      }
    }
  };

  const saveMessageToBackend = async (
    allMessages: Message[],
    customPrd?: any,
  ) => {
    if (backendProjectId) {
      const totalUsdCost = geminiService.getTotalCost();
      const lastCost = geminiService.getLastCost();
      setTotalAmount(totalUsdCost);

      const dataToSave = customPrd || prdData;
      const lastMessage = allMessages[allMessages.length - 1];
      // 只有当最后一条消息是 AI 发出的，才记录本次消耗金额
      const amountToRecord = lastMessage?.role === "ai" ? lastCost : 0;

      await dbService.saveChatMessage({
        project_id: backendProjectId,
        chat_record: allMessages,
        prd_data: getPrdDataForBackend(dataToSave),
        total_amount: amountToRecord,
        prd_progress: calculateProgress(dataToSave),
      });
    }
  };

  const [inspirationSetIndex, setInspirationSetIndex] = useState(0);
  const itemsPerPage = 4;
  const totalSets = 1;

  const scrollRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef<string | null>(null);

  const progress = useMemo(() => calculateProgress(prdData), [prdData]);
  const canAffordGen = points >= GENERATION_COST;
  const canAffordReality = points >= REALITY_COST;

  const currentProjectName =
    prdData.projectName || initialIdea.substring(0, 15) || "Unnamed Project";

  const safeString = (val: any): string => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") {
      try {
        return Object.entries(val)
          .map(
            ([k, v]) =>
              `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`,
          )
          .join(", ");
      } catch (e) {
        return JSON.stringify(val);
      }
    }
    return String(val);
  };

  const stripMarkdown = (str: string): string => {
    return str.replace(/\*\*/g, "");
  };

  useEffect(() => {
    setLang(initialLang);
  }, [initialLang]);

  useEffect(() => {
    if (initializedRef.current === projectId) return;
    const existing = historyRecords.find((r) => r.id === projectId);
    if (existing) {
      setInitialIdea(existing.initialIdea || "");
      setSelectedImage(existing.mainImage || null);
      setIsSubmittedForReality(
        !!existing.status && existing.status == "eng_evaluating",
      );
      setProjectNo(existing.projectNo || null);

      // If it's a remote project, the ID might be numeric
      if (existing.id && !isNaN(Number(existing.id))) {
        setBackendProjectId(Number(existing.id));
      }

      // const flatImages = existing.images || [];
      const chunked: string[][] = existing.ver_images || [];
      setVersionedImages(chunked);

      if (existing.nowVersion && existing.nowVersion > 0) {
        setViewVersionIndex(existing.nowVersion - 1);
      } else {
        setViewVersionIndex(Math.max(0, chunked.length - 1));
      }

      try {
        const parsedPrd = existing.prd ? JSON.parse(existing.prd) : existing;
        setPrdData(parsedPrd);
      } catch (e) {
        setPrdData({});
      }
      if (existing.messages && existing.messages.length > 0) {
        setMessages(existing.messages);
      }
      const initialUsd = existing.totalUsd || 0;
      setTotalAmount(initialUsd);
      geminiService.setInitialCost(initialUsd);
      setIsStarted(true);
    }
    initializedRef.current = projectId;
  }, [projectId, historyRecords]);

  const t = {
    zh: {
      mentorName: "AI Engineering Expert",
      mentorStatus: "Online - Structuring",
      title: "塑造您的造物蓝图",
      subtitle: "从第一行描述开始，AI 将协助您完善从功能到美学的每一个细节。",
      placeholder:
        "在这里写下您的想法。例如：我想做一个简约风格、支持 wireless 充电的折叠式台灯...",
      btnStart: "开启实验室",
      btnDirect: "直接生成外观",
      thinking: "正在组织语言...",
      welcome:
        "你好，我是你的项目专家。我已经为你提炼了产品名称并记录了核心描述，现在让我们从核心维度开始解构。首先是：",
      livePrd: "PRD",
      progress: "完成",
      pending: "点击手动编辑",
      visualStudio: "视觉渲染",
      btnGen: "生成视觉效果",
      btnRefine: "方案精调",
      btnRefining: "优化中...",
      btnRegen: "重新生成方案",
      genWait: "渲染解算中...",
      noImages: "PRD 定义中。您可以输入指令后点击“重新生成”以开启外观探索。",
      designRequirementPlaceholder:
        "输入您的设计需求，AI 将为您提供专业建议...",
      download: "导出图纸",
      renderHint: "包含影棚写实图与环境场景图",
      insufficient: "余额不足",
      costTag: `${GENERATION_COST} Credits`,
      directGenWait: "专家正在对齐创意并启动渲染...",
      exportPrd: "导出PRD",
      genRenders: "生成视觉效果",
      inspiration: "灵感案例",
      nextSet: "换一批",
      customPlaceholder: "或者输入您的自定义想法...",
      turnReality: "将创意变为实物",
      evaluating: "工程师评估中",
      realityCost: REALITY_COST > 0 ? `${REALITY_COST} 点` : "免费",
      prdIncomplete: "PRD 必填项尚未完成，请先补全以下核心信息：",
      submissionSuccessTitle: "创意已成功接收！",
      submissionSuccessDesc:
        "我们的硬件工程师正准备对方案进行物理评估。评估结果会在 24 小时内反馈至您的工作台，我们会通过邮件与您取得联系。",
      btnCreateNew: "创建新项目",
      btnBackToProjects: "回到我的项目",
      closeModal: "好的，我明白了",
      submitMandatory: "提交并开启实物转换",
      mandatoryTitle: "完善核心产品 definition",
      logVisuals: "生成外观渲染方案",
      logReality: "创意实物物理评估",
      assetsPool: "设计资产库",
      activeEngine: "PBR 物理渲染引擎已就绪",
      refinePlaceholder: "调整材质、配色或改变应用环境指令...",
      optTitle: "视觉微调指令",
      satisfactionTitle: "外观方案是否满意？",
      satisfactionDesc:
        "如果此方案符合预期，可请求我们的专业工程团队进行实物化物理评估。",
      satisfactionYes: "满意，开启实物评估",
      satisfactionNo: "不满意，继续精调",
      apiError: "系统繁忙或请求超限，请稍后再试。",
      retry: "立即重试",
      retryLabel: "正在重新尝试...",
      versionSelector: "版本切换",
      currentVersion: "版本",
      satisfactionQuestion:
        "以上分析是否解决了您的疑问？如果已解决，我们将继续完善 PRD 定义。",
      satisfactionCheckYes: "已解决，继续完善 PRD",
      satisfactionCheckNo: "还有疑问，继续探讨",
    },
    en: {
      mentorName: "AI Engineering Expert",
      mentorStatus: "Online - Structuring",
      title: "Design with Words",
      subtitle:
        "Generate aesthetics, define functionality, and bridge the gap to physical production.",
      placeholder: "Describe concept...",
      btnStart: "Start chatting",
      btnDirect: "Visualize now",
      thinking: "I'm thinking this through…",
      welcome:
        "Hello, I am your expert. I've refined the project name and description. Let's start with core dimensions. First:",
      livePrd: "PRD Specs",
      progress: "Specification Progress",
      pending: "Click to edit manually",
      visualStudio: "Rendering",
      btnGen: "Visualize now",
      btnRefine: "Refine Details",
      btnRefining: "Refining...",
      btnRegen: "Update Visuals",
      genWait: "Rendering...",
      noImages:
        "No renders yet.We need a bit more information about your product. Head back to PRD Specs to fill in the missing details.",
      designRequirementPlaceholder:
        "Enter design requirements, AI will provide expert advice...",
      download: "Save Image",
      renderHint: "Studio & lifestyle views",
      insufficient: "No Credits",
      costTag: `${GENERATION_COST} Credits`,
      directGenWait: "Expert aligning idea & starting renders...",
      exportPrd: "Export PDF",
      genRenders: "Visualize now",
      inspiration: "Inspirations",
      nextSet: "Next",
      turnReality: "Bring It to Life",
      evaluating: "Engineer Evaluating",
      realityCost: REALITY_COST > 0 ? `${REALITY_COST} Credits` : "Free",
      prdIncomplete: "Help us understand your requirements better.",
      submissionSuccessTitle: "Congratulations! ",
      submissionSuccessDesc:
        "Your design is now under technical review. We will send the feedback within 24 hours.",
      btnCreateNew: "Create New Project",
      btnBackToProjects: "Back to My Projects",
      closeModal: "Done",
      submitMandatory: "Submit for Engineering Review",
      mandatoryTitle: "Complete Core Specs",
      logVisuals: "Visual Design Rendering",
      logReality: "Physical Reality Evaluation",
      assetsPool: "Design Assets",
      activeEngine: "PBR Engine Active",
      refinePlaceholder:
        "Refine Your Design.Adjust material, colors, or scene details...",
      optTitle: "Design Refinements",
      satisfactionTitle: "Love this design?",
      satisfactionDesc: "Ask an engineering experts for manufacturing advice. ",
      satisfactionYes: "Let's go!",
      satisfactionNo: "Maybe later",
      apiError: "System busy or rate limited. Please try again.",
      retry: "Retry Now",
      retryLabel: "Retrying...",
      versionSelector: "Versions",
      currentVersion: "Ver",
      satisfactionQuestion:
        "Did the analysis resolve your questions? If so, we will continue refining the PRD definition.",
      satisfactionCheckYes: "Resolved, continue PRD",
      satisfactionCheckNo: "Still have questions",
    },
  }[lang];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isStarted && projectId) {
      const syncRecord = async () => {
        const flatImages = versionedImages.flat();
        const record: CreationRecord = {
          id: projectId,
          projectNo: projectNo || undefined,
          projectName: prdData.projectName,
          timestamp: Date.now(),
          initialIdea: initialIdea,
          prd: JSON.stringify(prdData),
          mainImage: selectedImage || "",
          images: flatImages,
          messages: messages,
          isSubmitted: isSubmittedForReality,
          ...prdData, // Spread structured fields
        };
        // await dbService.saveRecord(record);
        onComplete(record.prd || "", initialIdea, selectedImage || "");
      };
      const timeout = setTimeout(syncRecord, 500);
      return () => clearTimeout(timeout);
    }
  }, [
    prdData,
    selectedImage,
    versionedImages,
    messages,
    isStarted,
    projectId,
    isSubmittedForReality,
    projectNo,
  ]);

  const detectAndSetLanguage = (text: string) => {
    const englishCount = (text.match(/[a-zA-Z]/g) || []).length;
    const chineseCount = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
    if (englishCount > chineseCount * 1.5 && englishCount > 10) {
      setLang("en");
      return "en";
    }
    return lang;
  };

  const startRefining = async () => {
    if (!initialIdea.trim()) return;
    if (!user) {
      onAuthRequired();
      return;
    }
    const currentLang = detectAndSetLanguage(initialIdea);
    setIsStarted(true);
    const firstUserMsg: Message = { role: "user", content: initialIdea };
    setMessages([firstUserMsg]);
    setIsLoading(true);
    setFailedAction(null);

    try {
      const nameRes = await geminiService.extractProductName(
        initialIdea,
        currentLang,
      );
      const productName = nameRes.name;
      const rdtm_from = localStorage.getItem("source") || "";

      // 调用后端创建项目接口
      let bProjectId: number | null = null;
      try {
        const backendProject = await dbService.createProject({ project_name: productName, rdtm_from });
        setProjectNo(backendProject.project_no);
        bProjectId = Number(backendProject.project_id);
        setBackendProjectId(bProjectId);

        if (onProjectCreated) {
          onProjectCreated({
            id: bProjectId.toString(),
            projectNo: backendProject.project_no,
            projectName: productName,
            initialIdea: initialIdea,
            timestamp: Date.now(),
            // 获取路由参数 用户来源
            rdtm_from,
            mainImage: "",
            prd: "",
          });
        }
      } catch (e) {
        console.error("Backend project creation failed", e);
      }

      setPrdData((prev) => ({
        ...prev,
        projectName: productName,
        coreDescription: initialIdea,
      }));

      const firstField = DIALOGUE_GUIDED_STRUCTURE[0];
      const fieldLabel =
        firstField?.label[currentLang] ||
        firstField?.label["zh"] ||
        "Scenarios";
      const q = await geminiService.generateQuestion(
        initialIdea,
        [],
        currentLang,
        fieldLabel,
      );

      const welcomeVariations = [
        {
          zh: `嗨，很高兴你在这里。\n\n【${productName}】 是一个值得构建的大胆概念。\n\n让我们从核心概念开始，首先是: 【${firstField.label["en"]}】`,
          en: `Hi, amazing to have you here.\n\n【${productName}】 is a bold concept worth building.\n\nLet's start with core dimensions. First: 【${firstField.label["en"]}】`,
        },
        {
          zh: `欢迎！很高兴能与你一起探索。\n\n【${productName}】 展现了极具潜力的愿景。\n\n让我们深入挖掘核心细节，首先是: 【${firstField.label["en"]}】`,
          en: `Welcome! Excited to explore this with you.\n\n【${productName}】 represents a vision with immense potential.\n\nLet's dive into the core details. First: 【${firstField.label["en"]}】`,
        },
        {
          zh: `你好！准备好开启创意之旅了吗？\n\n【${productName}】 是一个非常有趣的切入点。\n\n让我们共同完善它的核心定义，首先是: 【${firstField.label["en"]}】`,
          en: `Hello! Ready to start this creative journey?\n\n【${productName}】 is a fascinating starting point.\n\nLet's refine its core definition together. First: 【${firstField.label["en"]}】`,
        },
        {
          zh: `很高兴见到你！让我们把想法变成现实。\n\n【${productName}】 的构思非常出色。\n\n让我们从最关键的维度开始构建，首先是: 【${firstField.label["en"]}】`,
          en: `Great to see you! Let's turn this idea into reality.\n\n【${productName}】 is an excellent concept.\n\nLet's build from the most critical dimension. First: 【${firstField.label["en"]}】`,
        },
      ];

      const randomIndex = Math.floor(Math.random() * welcomeVariations.length);
      const selectedVariation = welcomeVariations[randomIndex];
      const aiContent = `${selectedVariation[currentLang]}\n\n${q.data.question}`;

      const aiMsg: Message = {
        role: "ai",
        content: aiContent,
        options: q.data.options,
        answered: false,
        targetField: firstField.id,
      };
      const updatedMessages = [firstUserMsg, aiMsg];
      console.log("🚀 ~ startRefining ~ updatedMessages:", updatedMessages);
      setMessages(updatedMessages);
      console.log("🚀 ~ startRefining ~ messages:", messages);

      // Save all messages to backend
      if (bProjectId) {
        const initialPrd = {
          projectName: productName,
          coreDescription: initialIdea,
        };
        const totalUsd = geminiService.getTotalCost();
        const lastCost = geminiService.getLastCost();
        setTotalAmount(totalUsd);
        await dbService.saveChatMessage({
          project_id: bProjectId,
          chat_record: updatedMessages,
          prd_data: getPrdDataForBackend(initialPrd),
          prd_progress: calculateProgress(initialPrd),
          total_amount: lastCost,
        });
      }
    } catch (err) {
      console.error(err);
      setFailedAction({ fn: startRefining, label: t.apiError });
    } finally {
      setIsLoading(false);
    }
  };

  // 直接生成图片
  const handleDirectDesign = async () => {
    if (points < GENERATION_COST) {
      setToast({
        message:
          "Insufficient available points. You may contact customer service to apply for additional points.",
        type: "error",
      });
      return;
    }
    if (!initialIdea.trim()) return;
    if (!user) {
      onAuthRequired();
      return;
    }
    const currentLang = detectAndSetLanguage(initialIdea);
    setIsStarted(true);
    setRightPanelTab("visuals");
    setIsRightPanelExpanded(true);
    const firstUserMsg: Message = { role: "user", content: initialIdea };
    setMessages([firstUserMsg]);
    setIsLoading(true);
    setIsGeneratingImages(true);
    setFailedAction(null);

    try {
      // 1. 同步进行名称提炼和第一个问题的生成 (Parallelize name extraction and first question)
      const [nameRes, firstQRes] = await Promise.all([
        geminiService.extractProductName(initialIdea, currentLang),
        geminiService.generateQuestion(
          initialIdea,
          [],
          currentLang,
          DIALOGUE_GUIDED_STRUCTURE[0].label[currentLang] || "Scenarios",
        ),
      ]);

      const productName = nameRes.name;
      const rdtm_from = localStorage.getItem("source") || "";
      // 调用后端创建项目接口
      let bProjectId: number | null = null;
      try {
        const backendProject = await dbService.createProject({ rdtm_from, project_name: productName});
        setProjectNo(backendProject.project_no);
        bProjectId = Number(backendProject.project_id);
        setBackendProjectId(bProjectId);

        if (onProjectCreated) {
          onProjectCreated({
            id: bProjectId.toString(),
            projectNo: backendProject.project_no,
            projectName: productName,
            initialIdea: initialIdea,
            timestamp: Date.now(),
            // 获取路由参数 用户来源
            rdtm_from,
            mainImage: "",
            prd: "",
          });
        }
      } catch (e) {
        console.error("Backend project creation failed", e);
      }

      const firstField = DIALOGUE_GUIDED_STRUCTURE[0];
      const fieldLabel = firstField.label[currentLang] || "Scenarios";

      setPrdData({
        projectName: productName,
        coreDescription: initialIdea,
      });

      // 2. 组合 AI 消息：确认渲染启动 + 名称提炼 + 专家首个问题
      const welcomeText =
        currentLang === "zh"
          ? `收到！正在基于您的初始创意启动 4K 视觉渲染。项目已命名为：【${productName}】。在渲染过程中，让我们先完善核心定义。首先是：`
          : `Got it! Starting 4K visuals based on your idea. Project named: 【${productName}】. While we render, let's refine the core definition. First:`;

      const aiContent = `${welcomeText} 【${fieldLabel}】\n\n${firstQRes.data.question}`;
      const aiMsg: Message = {
        role: "ai",
        content: aiContent,
        options: firstQRes.data.options,
        answered: false,
        targetField: firstField.id,
      };
      const updatedMessages = [firstUserMsg, aiMsg];
      setMessages(updatedMessages);

      // Save all messages to backend
      if (bProjectId) {
        const initialPrd = {
          projectName: productName,
          coreDescription: initialIdea,
        };
        const totalUsd = geminiService.getTotalCost();
        const lastCost = geminiService.getLastCost();
        setTotalAmount(totalUsd);
        await dbService.saveChatMessage({
          project_id: bProjectId,
          chat_record: updatedMessages,
          prd_data: getPrdDataForBackend(initialPrd),
          prd_progress: calculateProgress(initialPrd),
          total_amount: lastCost,
        });
      }

      // 3. 执行渲染任务
      const newImages = await geminiService.generateImages(initialIdea, 4);
      if (newImages.urls.length > 0) {
        setVersionedImages([newImages.urls]);
        handleSwitchVersion(0);
        setSelectedImage(newImages.urls[0]);
        handleDeductPoints(
          GENERATION_COST,
          { zh: t.logVisuals, en: t.logVisuals },
          productName,
        );

        // 保存图片到后端
        if (bProjectId) {
          const lastCost = geminiService.getLastCost();
          dbService
            .saveProjectImages(bProjectId, newImages.urls, lastCost)
            .catch((e) => console.error("Failed to save project images", e));
        }

        if (!isSubmittedForReality) {
          setShowSatisfactionPrompt(true);
        }

        if (refreshHistory) refreshHistory();
      }
    } catch (err) {
      console.error(err);
      setFailedAction({ fn: handleDirectDesign, label: t.apiError });
    } finally {
      setIsLoading(false);
      setIsGeneratingImages(false);
    }
  };

  const handleAnswer = async (answer: any, msgIndex: number) => {
    const answerStr =
      typeof answer === "string" ? answer : String(answer || "");
    if (isLoading || !answerStr.trim()) return;
    const updatedMessages = [...messages];
    const currentMsg = updatedMessages[msgIndex];
    let nextPrd = prdData;

    if (currentMsg?.targetField === "satisfaction_check") {
      currentMsg.answered = true;
      const userMsg: Message = { role: "user", content: answerStr };
      setMessages((prev) => [...prev, userMsg]);

      if (answerStr === t.satisfactionCheckYes) {
        setRequirementAnalysisCount(0);
        // Resume PRD questions
        setIsLoading(true);
        try {
          const answeredGuidedIds = Object.keys(prdData).filter((id) => {
            const val = prdData[id];
            return (
              DIALOGUE_GUIDED_FIELDS.includes(id) &&
              val &&
              typeof val === "string" &&
              val.trim().length > 0
            );
          });
          const nextField = DIALOGUE_GUIDED_STRUCTURE.find(
            (f) => !answeredGuidedIds.includes(f.id),
          );

          if (!nextField) {
            setMessages((prev) => [
              ...prev,
              {
                role: "ai",
                content:
                  lang === "zh"
                    ? "核心维度的引导对话已完成。您可以随时切换视图检查 PRD 细节或生成视觉渲染图。"
                    : "Key guided dimensions are complete. You can switch tabs to check PRD details or generate renders now.",
              },
            ]);
            return;
          }

          const historyData: { q: string; a: string }[] = [];
          const allMsgs = [...messages, userMsg];
          for (let i = 0; i < allMsgs.length; i++) {
            if (allMsgs[i].role === "ai" && allMsgs[i + 1]?.role === "user") {
              historyData.push({
                q: allMsgs[i].content,
                a: allMsgs[i + 1].content,
              });
            }
          }
          const fieldLabel =
            nextField.label[lang] || nextField.label["zh"] || "Specs";
          const nextQ = await geminiService.generateQuestion(
            initialIdea,
            historyData,
            lang,
            fieldLabel,
          );
          const aiMsg: Message = {
            role: "ai",
            content: nextQ.data.question,
            options: cleanOptions(nextQ.data.options),
            answered: false,
            targetField: nextField.id,
          };
          setMessages((prev) => [...prev, aiMsg]);
        } catch (err) {
          console.error(err);
          setFailedAction({
            fn: () => handleAnswer(answerStr, msgIndex),
            label: t.apiError,
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        // Not resolved, reset count to allow more questions before asking again
        setRequirementAnalysisCount(0);
        setMessages((prev) => [
          ...prev,
          {
            role: "ai",
            content:
              lang === "zh"
                ? "没问题，请继续输入您的设计需求或疑问，我会尽力为您解答。"
                : "No problem, please continue entering your design requirements or questions, and I will do my best to answer them.",
          },
        ]);
      }
      return;
    }

    if (currentMsg?.targetField === "confirm_optimized_prompt") {
      currentMsg.answered = true;
      const userMsg: Message = { role: "user", content: answerStr };
      const nextMessages = [...updatedMessages, userMsg];
      setMessages(nextMessages);
      await saveMessageToBackend(nextMessages, nextPrd);

      if (
        answerStr === "确认并生成预览图" ||
        answerStr === "Confirm and Generate Preview"
      ) {
        handleGenerateImages(currentMsg.optimizedPrompt);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "ai",
            content:
              lang === "zh"
                ? "没问题，请继续输入您的设计需求或疑问。"
                : "No problem, please continue entering your design requirements or questions.",
          },
        ]);
      }
      return;
    }

    // 如果该消息有对应的 PRD 字段，更新 PRD 数据
    if (currentMsg?.targetField) {
      nextPrd = { ...prdData, [currentMsg.targetField!]: answerStr };
      setPrdData(nextPrd);
    }

    // 如果已经回答过（历史选项点击），仅更新数据
    if (currentMsg?.answered && msgIndex < messages.length - 1) {
      setMessages([...updatedMessages]);
      await saveMessageToBackend(updatedMessages, nextPrd);
      return;
    }

    if (currentMsg) {
      currentMsg.answered = true;
    }

    if (currentMsg?.targetField === "completion_choice") {
      const userMsg: Message = { role: "user", content: answerStr };
      const nextMessages = [...updatedMessages, userMsg];
      setMessages(nextMessages);
      await saveMessageToBackend(nextMessages, nextPrd);

      if (
        answerStr.includes("Generate Visuals Now") ||
        answerStr.includes("帮我生成渲染图")
      ) {
        handleGenerateImages();
      } else {
        // Continue discussing
        setIsLoading(true);
        try {
          const aiResponse =
            lang === "zh"
              ? "没问题，请问您还有什么关于产品的疑问或需要进一步完善的地方？我会尽力为您解答。"
              : "No problem. What other questions do you have about the product, or what else would you like to refine? I'm here to help.";
          const finalMessages = [
            ...nextMessages,
            { role: "ai" as const, content: aiResponse },
          ];
          setMessages(finalMessages);
          await saveMessageToBackend(finalMessages, nextPrd);
        } catch (err) {
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      }
      return;
    }

    const userMsg: Message = { role: "user", content: answerStr };
    const nextMessages = [...updatedMessages, userMsg];
    setMessages(nextMessages);
    await saveMessageToBackend(nextMessages, nextPrd); // Save user answer
    setCustomInputValue("");
    setFailedAction(null);
    const answeredGuidedIds = Object.keys(prdData).filter((id) => {
      const val = prdData[id];
      return (
        DIALOGUE_GUIDED_FIELDS.includes(id) &&
        val &&
        typeof val === "string" &&
        val.trim().length > 0
      );
    });
    // const answeredGuidedIds = Object.keys(prdData).filter(id => DIALOGUE_GUIDED_FIELDS.includes(id));
    if (
      currentMsg?.targetField &&
      !answeredGuidedIds.includes(currentMsg.targetField)
    ) {
      answeredGuidedIds.push(currentMsg.targetField);
    }

    const nextField = DIALOGUE_GUIDED_STRUCTURE.find(
      (f) => !answeredGuidedIds.includes(f.id),
    );

    if (!nextField) {
      const completionMsg =
        lang === "zh"
          ? "核心维度的引导对话已完成。您可以随时切换视图检查 PRD 细节或生成视觉渲染图。"
          : "Key guided dimensions are complete. You can switch tabs to check PRD details or generate visuals now.";
      const options =
        lang === "zh"
          ? [
              "帮我生成渲染图 Generate Visuals Now（消耗4 Credits）",
              "继续讨论需求",
            ]
          : [
              "Generate Visuals Now (Costs 4 Credits)",
              "Continue discussing requirements",
            ];
      const aiMsg: Message = {
        role: "ai",
        content: completionMsg,
        options,
        answered: false,
        targetField: "completion_choice",
      };
      const finalMessages: Message[] = [...nextMessages, aiMsg];
      setMessages(finalMessages);
      await saveMessageToBackend(finalMessages, nextPrd); // Save completion message
      return;
    }

    setIsLoading(true);
    try {
      const historyData: { q: string; a: string }[] = [];
      for (let i = 0; i < nextMessages.length; i++) {
        if (
          nextMessages[i].role === "ai" &&
          nextMessages[i + 1]?.role === "user"
        ) {
          historyData.push({
            q: nextMessages[i].content,
            a: nextMessages[i + 1].content,
          });
        }
      }
      const fieldLabel =
        nextField.label[lang] || nextField.label["zh"] || "Specs";
      const nextQ = await geminiService.generateQuestion(
        initialIdea,
        historyData,
        lang,
        fieldLabel,
      );
      const aiMsg: Message = {
        role: "ai",
        content: nextQ.data.question,
        options: cleanOptions(nextQ.data.options),
        answered: false,
        targetField: nextField.id,
      };
      const finalMessages = [...nextMessages, aiMsg];
      setMessages(finalMessages);
      await saveMessageToBackend(finalMessages, nextPrd); // Save AI question
    } catch (err) {
      console.error(err);
      setFailedAction({
        fn: () => handleAnswer(answerStr, msgIndex),
        label: t.apiError,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshOptions = async (msgIndex: number) => {
    if (isLoading) return;
    const updatedMessages = [...messages];
    const currentMsg = updatedMessages[msgIndex];

    if (
      !currentMsg ||
      !currentMsg.targetField ||
      currentMsg.targetField === "satisfaction_check"
    )
      return;

    setIsLoading(true);
    try {
      const historyData: { q: string; a: string }[] = [];
      // Build history up to this message
      for (let i = 0; i < msgIndex; i++) {
        if (
          updatedMessages[i].role === "ai" &&
          updatedMessages[i + 1]?.role === "user"
        ) {
          historyData.push({
            q: updatedMessages[i].content,
            a: updatedMessages[i + 1].content,
          });
        }
      }

      const field = FLAT_PRD_STRUCTURE.find(
        (f) => f.id === currentMsg.targetField,
      );
      const fieldLabel = field?.label[lang] || field?.label["zh"] || "Specs";

      const res = await geminiService.generateQuestion(
        initialIdea,
        historyData,
        lang,
        fieldLabel,
        true,
      );

      // Update the options of the current message
      currentMsg.options = cleanOptions(res.data.options);
      setMessages(updatedMessages);
      await saveMessageToBackend(updatedMessages);
    } catch (err) {
      console.error(err);
      setToast({ message: t.apiError, type: "error" });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDesignRequirementSubmit = async () => {
    if (isLoading || !requirementInput.trim()) return;

    const req = requirementInput.trim();
    setRequirementInput("");
    setDesignRequirement(req);

    const userMsg: Message = { role: "user", content: req };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      // Find the current active question/options context from messages
      const currentContextMsg = [...messages]
        .reverse()
        .find((m) => m.role === "ai" && m.options && !m.answered);
      const context = currentContextMsg
        ? {
            question: currentContextMsg.content,
            options: currentContextMsg.options,
          }
        : undefined;

      const res = await geminiService.analyzeDesignRequirement(
        initialIdea,
        req,
        lang,
        context,
      );

      const newCount = requirementAnalysisCount + 1;
      setRequirementAnalysisCount(newCount);

      // Extract optimized prompt if exists
      const promptMatch = res.text.match(
        /<optimized_prompt>(.*?)<\/optimized_prompt>/s,
      );
      const optimizedPrompt = promptMatch ? promptMatch[1].trim() : undefined;
      // Clean the text from the tag
      const cleanText = res.text
        .replace(/<optimized_prompt>.*?<\/optimized_prompt>/s, "")
        .trim();

      const aiMsg: Message = {
        role: "ai",
        content: cleanText,
        optimizedPrompt: optimizedPrompt,
        options: optimizedPrompt
          ? lang === "zh"
            ? ["确认并生成预览图", "暂不生成"]
            : ["Confirm and Generate Preview", "Not now"]
          : undefined,
        targetField: optimizedPrompt ? "confirm_optimized_prompt" : undefined,
        answered: false,
      };

      const messagesToAdd: Message[] = [aiMsg];

      if (!optimizedPrompt && newCount >= 2) {
        // After 2 or more analyses, ask if resolved
        messagesToAdd.push({
          role: "ai",
          content: t.satisfactionQuestion,
          options: [t.satisfactionCheckYes, t.satisfactionCheckNo],
          targetField: "satisfaction_check",
          answered: false,
        });
      }

      const finalMessages = [...messages, userMsg, ...messagesToAdd];
      setMessages(finalMessages);
      await saveMessageToBackend(finalMessages);
    } catch (err) {
      console.error(err);
      setFailedAction({ fn: handleDesignRequirementSubmit, label: t.apiError });
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualEdit = (fieldId: string) => {
    setEditingFieldId(fieldId);
    setEditValue(safeString(prdData[fieldId]));
  };

  const savePrdToBackend = async (updatedPrd: any, changedFieldId?: string) => {
    if (!backendProjectId) return;

    const fieldMapping: Record<string, string> = {
      projectName: "project_name",
      coreDescription: "description",
      scenariosPainPoints: "use_scenario_pain",
      userPersona: "user_portrait",
      appearanceMaterials: "appearance_material",
      featureDetails: "core_function",
      interactionOperation: "user_interaction",
      keyComponents: "key_component",
      controlLogic: "control_logic",
      performanceGoals: "performance_indicator",
      costBom: "cost_budget",
      mfgQuality: "quality_durability",
      sizePkg: "size_packaging",
    };

    const backendPrdData = getPrdDataForBackend(updatedPrd);

    const payload: any = {
      project_id: backendProjectId,
      prd_data: backendPrdData,
      total_amount: changedFieldId ? 0 : geminiService.getTotalCost(),
      prd_progress: calculateProgress(updatedPrd),
    };

    // If a specific field was changed, include it at the top level
    if (changedFieldId && fieldMapping[changedFieldId]) {
      const backendFieldName = fieldMapping[changedFieldId];
      payload[backendFieldName] =
        backendPrdData[backendFieldName as keyof typeof backendPrdData];
    }
    await dbService.saveChatMessage(payload);
  };

  const saveManualEdit = async () => {
    if (editingFieldId) {
      const updatedPrd = { ...prdData, [editingFieldId]: editValue };
      setPrdData(updatedPrd);
      await savePrdToBackend(updatedPrd, editingFieldId);
      setEditingFieldId(null);
    }
  };

  const handleGenerateImages = async (customPrompt?: string) => {
    if (points < GENERATION_COST) {
      setToast({
        message:
          "Insufficient available points. You may contact customer service to apply for additional points.",
        type: "error",
      });
      return;
    }
    // Use refinementPrompt state if no customPrompt is passed
    const activePrompt = customPrompt || refinementPrompt;
    if (activePrompt && !customPrompt) {
      setRefinementPrompt(""); // Clear it if we're using it from state
    }

    setRightPanelTab("visuals");
    setIsRightPanelExpanded(true);
    setIsGeneratingImages(true);
    setShowSatisfactionPrompt(false);
    setFailedAction(null);
    try {
      const prdSummary = JSON.stringify(prdData);
      const combinedPrompt = designRequirement
        ? `${prdSummary || initialIdea}. Additional Requirements: ${designRequirement}`
        : prdSummary || initialIdea;
      const newImages = await geminiService.generateImages(
        combinedPrompt,
        4,
        activePrompt,
        selectedImage || undefined,
      );

      if (newImages.urls.length > 0) {
        setVersionedImages((prev) => {
          return [...prev, newImages.urls];
        });

        handleSwitchVersion(versionedImages.length);
        setSelectedImage(newImages.urls[0]);
        handleDeductPoints(
          GENERATION_COST,
          { zh: t.logVisuals, en: t.logVisuals },
          currentProjectName,
        );

        // 保存图片到后端
        if (backendProjectId) {
          const lastCost = geminiService.getLastCost();
          dbService
            .saveProjectImages(
              backendProjectId,
              newImages.urls,
              lastCost,
              customPrompt,
            )
            .catch((e) => console.error("Failed to save project images", e));
        }

        if (!isSubmittedForReality) {
          setTimeout(() => setShowSatisfactionPrompt(true), 1500);
        }

        if (refreshHistory) refreshHistory();
      }
    } catch (err) {
      console.error(err);
      setFailedAction({
        fn: () => handleGenerateImages(customPrompt),
        label: t.apiError,
      });
    } finally {
      setIsGeneratingImages(false);
    }
  };

  const handleTurnToReality = () => {
    setShowSatisfactionPrompt(false);
    if (isSubmittedForReality || !selectedImage) return;

    if (points < REALITY_COST) {
      setToast({ message: t.insufficient, type: "error" });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    const missingFields = MANDATORY_FIELDS.filter((f) => !prdData[f]);
    if (missingFields.length > 0) {
      const initialTempData: Record<string, string> = {};
      missingFields.forEach((f) => (initialTempData[f] = ""));
      setTempMandatoryData(initialTempData);
      setShowFillMandatoryModal(true);
      return;
    }

    submitRealityEvaluation();
  };

  const submitRealityEvaluation = () => {
    if (REALITY_COST > 0) {
      handleDeductPoints(
        REALITY_COST,
        { zh: t.logReality, en: t.logReality },
        currentProjectName,
      );
    }
    setShowFillMandatoryModal(false);
    requestEngineeringEvaluation();
  };

  // 请求工程评估
  const requestEngineeringEvaluation = async () => {
    setIsLoading(true);
    try {
      const evaluation = await dbService.requestEngineeringEvaluation({
        project_id: backendProjectId,
      });
      if (evaluation.code == 1) {
        setIsSubmittedForReality(true);
        setShowRealitySuccessModal(true);
        if (refreshHistory) refreshHistory();
      } else {
        throw new Error(
          evaluation.msg || "Failed to request engineering evaluation",
        );
      }
    } catch (err) {
      console.error(err);
      setFailedAction({
        fn: requestEngineeringEvaluation,
        label: err || t.apiError,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMandatorySubmit = async () => {
    const unfilled = MANDATORY_FIELDS.filter(
      (f) => !prdData[f] && !tempMandatoryData[f]?.trim(),
    );
    if (unfilled.length > 0) {
      setToast({
        message:
          lang === "zh"
            ? "请完成所有必填核心定义后再提交。"
            : "Please fill all core fields before submitting.",
        type: "info",
      });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    const updatedPrd = {
      ...prdData,
      ...tempMandatoryData,
    };
    setPrdData(updatedPrd);
    await savePrdToBackend(updatedPrd);

    submitRealityEvaluation();
  };

  const handleExportPrd = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const sectionsHtml = PRD_HIERARCHY.map(
      (group) => `
      <div style="margin-bottom: 30px;">
        <h2 style="color: #ef4444; border-bottom: 2px solid #fee2e2; padding-bottom: 8px; font-size: 18px;">${group.label[lang]}</h2>
        <div style="display: grid; gap: 15px; margin-top: 15px;">
          ${group.items
            .map(
              (item) => `
            <div style="background: #f8fafc; padding: 15px; border-radius: 12px; border-left: 4px solid #e2e8f0;">
              <strong style="display: block; font-size: 12px; color: #94a3b8; margin-bottom: 5px;">${item.label[lang]}</strong>
              <div style="font-size: 14px; color: #1e293b; line-height: 1.6; white-space: pre-wrap;">${stripMarkdown(safeString(prdData[item.id])) || '<span style="color: #cbd5e1;">-</span>'}</div>
            </div>
          `,
            )
            .join("")}
        </div>
      </div>
    `,
    ).join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>PRD Export - ${safeString(prdData.projectName) || "AI Project"}</title>
          <style>
            body { font-family: 'Inter', sans-serif; padding: 40px; color: #0f172a; max-width: 800px; margin: 0 auto; line-height: 1.5; }
            h1 { font-size: 32px; font-weight: 900; letter-spacing: -1px; margin-bottom: 10px; color: #000; }
            .header { border-bottom: 4px solid #000; padding-bottom: 20px; margin-bottom: 40px; display: flex; justify-content: space-between; align-items: flex-end; }
            .meta { font-size: 12px; font-weight: bold; color: #64748b; }
            @media print { .no-print { display: none; } body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="meta">Product Requirement Document</div>
              <h1>${safeString(prdData.projectName) || "AI Creator Project"}</h1>
            </div>
            <div class="meta" style="text-align: right;">Generated: ${new Date().toLocaleDateString()}</div>
          </div>
          ${sectionsHtml}
          <div style="margin-top: 50px; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 20px; font-size: 12px; color: #94a3b8; letter-spacing: 2px;">
            Confidential Document - Generated by AI Creator Lab
          </div>
          <script>
            window.onload = () => {
              setTimeout(() => {
                window.print();
              }, 500);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const lastAiMessage = [...messages].reverse().find((m) => m.role === "ai");
  const highlightedFieldId = lastAiMessage?.targetField;

  const rightPanelWidth = !isRightPanelExpanded
    ? "w-[64px]"
    : rightPanelTab === "prd"
      ? "w-[500px]"
      : "w-[860px]";

  const currentViewImages = versionedImages[viewVersionIndex] || [];

  return (
    <div className="flex-1 flex flex-col bg-transparent overflow-hidden relative">
      {failedAction && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[300] w-[90%] max-w-lg animate-fade-up">
          <div className="bg-white/90 p-5 rounded-2xl border border-red-100 shadow-2xl flex items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-black text-red-600 tracking-widest leading-none mb-1">
                  Error Detected
                </span>
                <span className="text-[13px] font-black text-black tracking-tight leading-none">
                  {failedAction.label}
                </span>
              </div>
            </div>
            <button
              onClick={() => {
                const fn = failedAction.fn;
                setFailedAction(null);
                fn();
              }}
              className="px-6 py-2.5 bg-red-600 text-white rounded-lg text-[11px] font-black tracking-widest flex items-center gap-2 hover:bg-red-700 hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-red-100"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {t.retry}
            </button>
          </div>
        </div>
      )}

      {showFillMandatoryModal && (
        <div className="fixed inset-0 z-[400] bg-slate-950/70 flex items-center justify-center p-6 animate-fade-up">
          <div className="max-w-3xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
            <div className="px-10 py-8 border-b border-slate-50 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-50 rounded-xl">
                  <Edit3 className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-black text-black tracking-tight">
                  {t.mandatoryTitle}
                </h2>
              </div>
              <button
                onClick={() => setShowFillMandatoryModal(false)}
                className="w-10 h-10 flex items-center justify-center hover:bg-slate-100 rounded-full transition-all text-slate-400 hover:text-slate-600 hover:scale-110 active:scale-90"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-10 custom-scrollbar space-y-8 bg-slate-50/20">
              <p className="text-sm font-bold text-slate-500 mb-6 italic">
                {t.prdIncomplete}
              </p>
              {PRD_HIERARCHY.map((group) => {
                const missingInGroup = group.items.filter(
                  (it) => MANDATORY_FIELDS.includes(it.id) && !prdData[it.id],
                );
                if (missingInGroup.length === 0) return null;
                return (
                  <div key={group.id} className="space-y-6">
                    <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                      <span className="text-[11px] font-black text-slate-300 tracking-widest">
                        {group.label[lang]}
                      </span>
                    </div>
                    <div className="space-y-6">
                      {missingInGroup.map((it) => (
                        <div key={it.id} className="space-y-3">
                          <label className="text-[12px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                            {it.icon} {it.label[lang]}
                          </label>
                          <textarea
                            value={tempMandatoryData[it.id] || ""}
                            onChange={(e) =>
                              setTempMandatoryData((prev) => ({
                                ...prev,
                                [it.id]: e.target.value,
                              }))
                            }
                            placeholder={
                              lang === "zh"
                                ? `请输入${it.label.zh}...`
                                : `Enter ${it.label.en}...`
                            }
                            className="w-full p-5 bg-white border border-slate-100 rounded-xl text-[13px] font-bold text-black outline-none focus:border-red-600 h-16 resize-none shadow-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-10 border-t border-slate-50 bg-white shrink-0 space-y-6">
              <p className="text-[10px] text-slate-400 leading-relaxed text-center font-medium">
                We use your inputs and generated content solely for engineering
                review and service delivery. All information is handled
                confidentially and never disclosed to third parties without
                authorization.
              </p>
              <button
                onClick={handleMandatorySubmit}
                className="w-full py-3.5 bg-gradient-to-r from-[#ff0055] via-[#ff5e00] to-[#9900ff] text-white rounded-lg text-[14px] font-black tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-red-200"
              >
                {t.submitMandatory}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRealitySuccessModal && (
        <div className="fixed inset-0 z-[401] bg-slate-950/60 flex items-center justify-center p-6 animate-fade-up">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 p-10 flex flex-col items-center text-center">
            <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mb-8 relative">
              <div className="absolute inset-0 bg-green-500/20 blur-2xl rounded-full"></div>
              <Rocket className="w-10 h-10 text-green-600 relative" />
            </div>
            <h2 className="text-2xl font-black text-black tracking-tight mb-4">
              {t.submissionSuccessTitle}
            </h2>
            <p className="text-sm font-medium text-slate-500 leading-relaxed mb-10">
              {t.submissionSuccessDesc}
            </p>
            <div className="w-full flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowRealitySuccessModal(false);
                  if (onNewProject) onNewProject();
                }}
                className="w-full py-3.5 bg-red-600 text-white rounded-lg text-[13px] font-black tracking-widest hover:bg-red-700 hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-red-200"
              >
                {t.btnCreateNew}
              </button>
              <button
                onClick={() => setShowRealitySuccessModal(false)}
                className="w-full py-3.5 bg-slate-100 text-slate-600 rounded-lg text-[13px] font-black tracking-widest hover:bg-slate-200 hover:scale-[1.02] active:scale-95 transition-all"
              >
                {t.btnBackToProjects}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[800] animate-fade-up">
          <div
            className={`px-8 py-4 rounded-xl shadow-2xl flex items-center gap-4 border ${
              toast.type === "success"
                ? "bg-green-50/90 border-green-200 text-green-700"
                : toast.type === "error"
                  ? "bg-red-50/90 border-red-200 text-red-700"
                  : "bg-blue-50/90 border-blue-200 text-blue-700"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : toast.type === "error" ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <Info className="w-5 h-5" />
            )}
            <span className="text-sm font-black tracking-tight leading-tight">
              {toast.message}
            </span>
          </div>
        </div>
      )}

      {zoomImageUrl && (
        <div
          className="fixed inset-0 z-[200] bg-slate-950/90 flex items-center justify-center p-4 lg:p-20 animate-fade-up"
          onClick={() => setZoomImageUrl(null)}
        >
          <button className="absolute top-10 right-10 text-white/40 hover:text-white hover:scale-110 active:scale-90 transition-all">
            <X className="w-10 h-10" />
          </button>
          <div
            className="relative max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoomImageUrl}
              className="max-w-full max-h-full object-contain rounded-2xl shadow-[0_0_100px_rgba(220,38,38,0.2)]"
            />
          </div>
        </div>
      )}

      {!isStarted ? (
        <div className="flex-1 overflow-y-auto custom-scrollbar relative z-10 flex flex-col items-center bg-transparent pt-12 lg:pt-20">
          <div className="max-w-5xl mx-auto w-full flex flex-col items-center p-6 lg:p-10 space-y-8">
            <div className="space-y-4 animate-fade-up text-center">
              <h1 className="text-5xl md:text-6xl font-black text-black leading-[1.05] tracking-tighter drop-shadow-sm select-none">
                {t.title}
              </h1>
              <p className="text-lg font-medium text-slate-400 max-w-2xl mx-auto leading-relaxed">
                {t.subtitle}
              </p>
            </div>

            <div
              className="relative animate-fade-up w-full"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="bg-white/20 rounded-2xl border border-white/80 shadow-xl overflow-hidden p-2 ring-1 ring-black/5 hover:shadow-2xl transition-all duration-1000">
                <div className="relative">
                  <TypewriterTextarea
                    value={initialIdea}
                    onChange={(e) => setInitialIdea(e.target.value)}
                    lang={lang}
                    words={TYPEWRITER_WORDS}
                    className="w-full h-48 p-6 bg-transparent outline-none resize-none text-xl font-bold text-black placeholder:text-slate-500 transition-all focus:placeholder:text-slate-100"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white/10 rounded-b-2xl border-t border-white/40 shadow-inner">
                  <button
                    onClick={handleDirectDesign}
                    disabled={!initialIdea.trim() || isLoading}
                    className="group px-6 py-3.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-[14px] font-black flex items-center gap-3 hover:bg-red-100 hover:border-red-300 hover:scale-[1.02] active:scale-95 transition-all shadow-[0_20px_40px_-15px_rgba(220,38,38,0.1)] disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FastForward className="w-4 h-4 text-red-600" />
                    )}
                    <div className="flex flex-col items-start leading-tight">
                      <span>{t.btnDirect}</span>
                      <span className="text-[9px] font-black text-red-500/60 tracking-widest mt-0.5 flex items-center gap-1">
                        <Zap className="w-2 h-2 fill-current" /> {t.costTag}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={startRefining}
                    disabled={!initialIdea.trim() || isLoading}
                    className="relative px-10 py-3.5 bg-gradient-to-r from-[#ff0055] via-[#ff5e00] to-[#9900ff] bg-[length:200%_auto] text-white rounded-lg text-[14px] font-black flex items-center gap-4 hover:bg-right hover:scale-[1.03] active:scale-95 transition-all duration-500 disabled:opacity-50 group shadow-[0_20px_60px_-15px_rgba(255,0,85,0.4)]"
                  >
                    <div className="relative z-10 flex items-center gap-3">
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Zap className="w-5 h-5 fill-current group-hover:animate-pulse transition-transform group-hover:rotate-12" />
                      )}
                      <span className="tracking-widest font-black">
                        {t.btnStart}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <div
              className="w-full animate-fade-up mt-12"
              style={{ animationDelay: "0.2s" }}
            >
              <div className="flex items-center justify-between px-6 mb-3">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-4 h-4 text-red-600" />
                  <span className="text-[12px] font-black text-slate-400 tracking-widest">
                    {t.inspiration}
                  </span>
                </div>
              </div>
              <div className="relative px-6 overflow-hidden group/marquee">
                <div className="flex gap-6 animate-marquee-left hover:[animation-play-state:paused] w-max pb-8">
                  {[...INSPIRATION_IDEAS, ...INSPIRATION_IDEAS].map(
                    (idea, index) => (
                      <button
                        key={`${idea.id}-${index}`}
                        onClick={() => setSelectedInspiration(idea)}
                        className="flex-shrink-0 w-[320px] md:w-[450px] group relative bg-white/60 border border-white/80 rounded-2xl overflow-hidden transition-all hover:bg-white hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.08)] active:scale-[0.98] flex flex-row items-center shadow-sm border-slate-100"
                      >
                        <div className="w-28 h-28 md:w-36 md:h-36 flex-shrink-0 relative overflow-hidden">
                          <img
                            src={idea.image}
                            alt={idea[lang]}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            referrerPolicy="no-referrer"
                          />
                          <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors" />
                        </div>
                        <div className="p-5 flex-1 flex flex-col justify-center text-left min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="text-red-600 opacity-60 scale-75 origin-left">
                              {idea.icon}
                            </div>
                            <span className="text-[9px] font-black text-slate-400 tracking-wider">
                              Case Study
                            </span>
                          </div>
                          <h3 className="text-[14px] font-black text-slate-900 truncate leading-tight mb-1">
                            {idea[lang]}
                          </h3>
                          <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                            {idea.details.description[lang]}
                          </p>
                        </div>
                        <div className="absolute right-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-7 h-7 bg-slate-900 rounded-full flex items-center justify-center text-white">
                            <ArrowRight className="w-3.5 h-3.5" />
                          </div>
                        </div>
                      </button>
                    ),
                  )}
                </div>
                {/* Gradient Masks for smooth edges */}
                <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none" />
                <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex min-h-0 bg-white/40 overflow-hidden relative z-10 border-t border-slate-100/50">
          <div className="flex-1 flex flex-col min-0 min-w-0 bg-white/40 z-10 relative transition-all overflow-hidden border-r border-slate-100/50">
            <div className="h-16 shrink-0 px-8 border-b border-slate-50 flex items-center justify-between bg-white/20">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center shadow-lg">
                  <Bot className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <h3 className="text-[13px] font-black text-black tracking-tight leading-none">
                    {t.mentorName}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span className="text-[11px] font-bold text-slate-400 tracking-widest leading-none">
                      {t.mentorStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-10 bg-slate-50/5"
            >
              <div className="max-w-2xl mx-auto space-y-10 pb-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-32 animate-fade-up">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-red-600/20 blur-2xl rounded-full"></div>
                      <Loader2 className="w-10 h-10 animate-spin text-red-600 relative" />
                    </div>
                    <p className="text-[12px] font-black tracking-[0.1em] text-slate-400 animate-pulse">
                      {t.thinking}
                    </p>
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const isLatestAi =
                      msg.role === "ai" && idx === messages.length - 1;
                    return (
                      <div
                        key={idx}
                        className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""} animate-fade-up`}
                      >
                        <div
                          className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center border shadow-sm ${msg.role === "user" ? "bg-slate-100 border-slate-200" : "bg-white/80 border-slate-100"}`}
                        >
                          {msg.role === "user" ? (
                            <User className="w-3.5 h-3.5 text-slate-500" />
                          ) : (
                            <Bot className="w-3.5 h-3.5 text-slate-400" />
                          )}
                        </div>
                        <div className="flex flex-col gap-2 max-w-[92%]">
                          <div
                            className={`px-4 py-3 rounded-xl border shadow-sm ${msg.role === "user" ? "bg-slate-100 border-slate-200 text-black rounded-tr-none font-normal text-[13px]" : "bg-white/90 border-slate-100 text-black rounded-tl-none font-normal text-[13px]"}`}
                          >
                            <div className="markdown-body leading-relaxed prose prose-slate max-w-none">
                              <ReactMarkdown
                                children={safeString(msg.content)}
                              />
                            </div>
                          </div>

                          {msg.role === "ai" && msg.options && (
                            <div className="flex flex-col gap-3 pl-1 mt-4 animate-fade-up">
                              <div className="flex flex-col gap-1.5">
                                {msg.options.map((opt, i) => {
                                  const isSelected =
                                    prdData[msg.targetField!] === opt;
                                  return (
                                    <button
                                      key={i}
                                      onClick={() => handleAnswer(opt, idx)}
                                      disabled={isLoading}
                                      className={`w-full px-4 py-3 border rounded-lg text-left transition-all flex items-center justify-between group shadow-sm ${
                                        isSelected
                                          ? "bg-red-50 border-red-600 ring-2 ring-red-600/10"
                                          : "bg-white border-slate-100 hover:border-red-400 hover:bg-white"
                                      } hover:scale-[1.01] active:scale-95`}
                                    >
                                      <div
                                        className={`text-[13px] font-normal tracking-tight markdown-body-compact ${isSelected ? "text-black" : "text-black group-hover:text-black"}`}
                                      >
                                        <ReactMarkdown
                                          children={safeString(opt)}
                                        />
                                      </div>
                                      {isSelected ? (
                                        <CheckCircle2 className="w-4 h-4 text-red-600" />
                                      ) : (
                                        <ArrowRight className="w-4 h-4 text-slate-200 group-hover:text-red-400 transition-colors" />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Refresh Button at bottom-left */}
                              {!msg.answered &&
                                msg.targetField !== "satisfaction_check" && (
                                  <div className="flex items-center justify-start">
                                    <button
                                      onClick={() => handleRefreshOptions(idx)}
                                      disabled={isLoading}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/50 hover:bg-white border border-slate-100 rounded-lg text-[10px] font-black text-slate-400 hover:text-red-500 hover:scale-105 active:scale-95 transition-all group shadow-sm disabled:opacity-50"
                                      title={
                                        lang === "zh"
                                          ? "换一批更落地的选项"
                                          : "Refresh for more practical options"
                                      }
                                    >
                                      <RefreshCw
                                        className={`w-3 h-3 ${isLoading ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`}
                                      />
                                      <span className="tracking-widest">
                                        {lang === "zh"
                                          ? "换一批"
                                          : "Show more options"}
                                      </span>
                                    </button>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {isLoading && (
                  <div className="flex gap-4 animate-fade-up">
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-white/80 border border-slate-100 shadow-sm flex items-center justify-center">
                      <Bot className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="bg-white/60 px-5 py-3 rounded-2xl rounded-tl-none border border-slate-100 flex items-center gap-3 shadow-sm">
                      <Loader2 className="w-4 h-4 text-red-600 animate-spin" />
                      <span className="text-[12px] font-black text-slate-400 tracking-widest">
                        {isGeneratingImages ? t.directGenWait : t.thinking}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* New Design Requirement Input Bar */}
            <div className="shrink-0 p-4 bg-white border-t border-slate-100/50">
              <div className="max-w-2xl mx-auto flex items-center gap-3">
                <div className="flex-1 relative group">
                  <input
                    type="text"
                    value={requirementInput}
                    onChange={(e) => setRequirementInput(e.target.value)}
                    disabled={isLoading}
                    placeholder={t.designRequirementPlaceholder}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        requirementInput.trim() &&
                        !isLoading
                      ) {
                        handleDesignRequirementSubmit();
                      }
                    }}
                    className="w-full pl-6 pr-16 py-2.5 bg-white border border-slate-100 rounded-2xl outline-none text-[14px] font-bold text-black focus:border-red-600 focus:ring-4 focus:ring-red-600/5 transition-all shadow-xl shadow-slate-200/50"
                  />
                  <button
                    onClick={handleDesignRequirementSubmit}
                    disabled={isLoading || !requirementInput.trim()}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center hover:bg-red-600 hover:scale-105 active:scale-95 transition-all shadow-lg disabled:opacity-50 group/btn"
                  >
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>

                {versionedImages.length === 0 && (
                  <button
                    onClick={() => handleGenerateImages()}
                    disabled={isLoading || isGeneratingImages}
                    className="shrink-0 h-10 px-6 bg-gradient-to-r from-[#ff1b6b] to-[#ff4500] text-white rounded-xl text-[11px] font-black tracking-widest flex items-center justify-center gap-3 transition-all duration-300 shadow-xl shadow-red-100 disabled:opacity-50 overflow-hidden group hover:scale-[1.02] active:scale-95"
                  >
                    {isGeneratingImages ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ImageIcon className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">{t.genRenders}</span>
                    <span className="text-[10px] opacity-80 font-black tracking-widest flex items-center gap-1 bg-black/10 px-2 py-0.5 rounded-md ml-1">
                      ⚡ {t.costTag}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            className={`hidden lg:flex flex-col bg-slate-50 shrink-0 z-20 overflow-hidden shadow-2xl transition-all duration-300 ease-in-out relative border-l border-slate-100 ${rightPanelWidth}`}
          >
            <div
              className={`h-14 shrink-0 bg-white border-b border-slate-100 flex items-center px-5 ${isRightPanelExpanded ? "justify-between" : "justify-center"}`}
            >
              {isRightPanelExpanded ? (
                <>
                  <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-full shadow-inner border border-slate-200">
                    <button
                      onClick={() => setRightPanelTab("prd")}
                      className={`px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest transition-all flex items-center gap-2 ${rightPanelTab === "prd" ? "bg-white text-red-600 shadow-sm scale-[1.02]" : "text-slate-400 hover:text-slate-600 hover:scale-105 active:scale-95"}`}
                    >
                      <LayoutList className="w-3.5 h-3.5" />
                      {t.livePrd}
                    </button>
                    <button
                      onClick={() => setRightPanelTab("visuals")}
                      className={`px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest transition-all flex items-center gap-2 ${rightPanelTab === "visuals" ? "bg-white text-red-600 shadow-sm scale-[1.02]" : "text-slate-400 hover:text-slate-600 hover:scale-105 active:scale-95"}`}
                    >
                      <Palette className="w-3.5 h-3.5" />
                      {t.visualStudio}
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    {rightPanelTab === "visuals" && (
                      <>
                        {versionedImages.length > 0 && (
                          <div className="relative">
                            <button
                              onClick={handleTurnToReality}
                              disabled={
                                isSubmittedForReality ||
                                !selectedImage ||
                                !canAffordReality
                              }
                              className={`flex items-center gap-2.5 px-6 h-9 rounded-lg transition-all text-[11px] font-black tracking-widest shadow-xl shadow-slate-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                                isSubmittedForReality
                                  ? "bg-green-100 text-green-700 border border-green-200"
                                  : "bg-gradient-to-r from-[#ff0055] via-[#ff5e00] to-[#9900ff] bg-[length:200%_auto] text-white hover:bg-right hover:scale-[1.02] active:scale-95"
                              }`}
                            >
                              {isSubmittedForReality ? (
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              ) : (
                                <Zap className="w-3.5 h-3.5 text-yellow-300 fill-current" />
                              )}
                              {isSubmittedForReality
                                ? t.evaluating
                                : t.turnReality}
                            </button>
                            {!isSubmittedForReality && (
                              <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-red-600 text-white text-[8px] font-black rounded-full shadow-lg whitespace-nowrap z-10 border border-white/20">
                                1 free review available
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => setIsRightPanelExpanded(false)}
                      className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-red-600 transition-all"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => setIsRightPanelExpanded(true)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-red-600 transition-all flex flex-col items-center gap-2"
                >
                  {rightPanelTab === "prd" ? (
                    <PanelsLeftBottom className="w-5 h-5" />
                  ) : (
                    <PanelsRightBottom className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>

            {isRightPanelExpanded && (
              <div className="flex-1 flex flex-col min-h-0 bg-white">
                {rightPanelTab === "prd" ? (
                  <div className="flex-1 flex flex-col overflow-hidden animate-fade-up">
                    <div className="px-6 py-4 bg-slate-50/50 flex flex-col gap-3 border-b border-slate-100">
                      <div className="w-full">
                        <div className="flex justify-between items-center mb-1.5">
                          <span className="text-[10px] font-black text-slate-400 tracking-widest">
                            {t.progress}
                          </span>
                          <span className="text-[10px] font-black text-red-600 tracking-tighter">
                            {progress}%
                          </span>
                        </div>
                        <div className="relative h-1.5 bg-slate-200 rounded-full overflow-hidden shadow-inner">
                          <div
                            className="absolute inset-y-0 left-0 bg-red-600 transition-all duration-1000 ease-out shadow-[0_0_15px_rgba(220,38,38,0.4)]"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleExportPrd}
                          title={t.exportPrd}
                          className="flex-1 h-9 border border-slate-200 bg-white text-slate-500 rounded-lg text-[10px] font-black tracking-widest flex items-center justify-center gap-2 hover:bg-slate-50 hover:text-black hover:scale-[1.02] active:scale-95 transition-all shadow-sm"
                        >
                          <FileDown className="w-4 h-4" />
                          {t.exportPrd}
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar p-5 bg-slate-50/20">
                      <div className="max-w-xl mx-auto space-y-6">
                        {PRD_HIERARCHY.map((group) => (
                          <div key={group.id} className="space-y-3">
                            <h4 className="px-2 text-[11px] font-black text-slate-400 tracking-[0.2em]">
                              {group.label[lang]}
                            </h4>
                            <div className="space-y-2.5">
                              {group.items.map((field) => {
                                const answer = prdData[field.id];
                                const isCompleted = !!answer;
                                const isEditing = editingFieldId === field.id;
                                const isHighlighted =
                                  highlightedFieldId === field.id;

                                return (
                                  <div
                                    key={field.id}
                                    onClick={() =>
                                      !isEditing && handleManualEdit(field.id)
                                    }
                                    className={`p-4 rounded-xl border transition-all duration-500 group/item relative cursor-pointer ${
                                      isHighlighted
                                        ? "bg-red-50/40 border-red-600 shadow-[0_8px_30px_rgba(220,38,38,0.06)] scale-[1.02] z-10"
                                        : isCompleted
                                          ? "bg-white border-slate-100 shadow-sm"
                                          : "bg-transparent border-dashed border-slate-200 opacity-60 hover:opacity-100"
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-3 mb-1.5">
                                      <div className="flex items-center gap-2.5">
                                        <div
                                          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isHighlighted ? "bg-red-600 text-white" : isCompleted ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-300"}`}
                                        >
                                          {field.icon}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={`text-[10px] font-black tracking-[0.2em] ${isHighlighted || isCompleted ? "text-slate-400" : "text-slate-400"}`}
                                          >
                                            {field.label[lang]}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    {isEditing ? (
                                      <div
                                        className="mt-2 flex gap-2"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <textarea
                                          value={editValue}
                                          onChange={(e) =>
                                            setEditValue(e.target.value)
                                          }
                                          className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-lg text-[13px] font-medium text-black outline-none focus:border-red-600 h-24 resize-none shadow-inner"
                                          autoFocus
                                        />
                                        <div className="flex flex-col gap-2">
                                          <button
                                            onClick={saveManualEdit}
                                            className="p-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 hover:scale-110 active:scale-90 transition-all shadow-md"
                                          >
                                            <Check className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() =>
                                              setEditingFieldId(null)
                                            }
                                            className="p-2.5 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 hover:scale-110 active:scale-90 transition-all shadow-md"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="mt-0.5">
                                        {isCompleted ? (
                                          <div className="text-[13px] font-medium text-black leading-relaxed whitespace-pre-wrap">
                                            {stripMarkdown(safeString(answer))}
                                          </div>
                                        ) : (
                                          <span className="text-[10px] font-black text-slate-300 tracking-[0.2em]">
                                            {t.pending}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col overflow-hidden animate-fade-up bg-slate-950">
                    <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                      {isGeneratingImages && (
                        <GenerationProgress
                          lang={lang}
                          statusMessages={RENDER_STATUS_MESSAGES}
                        />
                      )}

                      {selectedImage ? (
                        <div className="w-full h-full flex flex-col relative group">
                          <div className="absolute top-6 left-6 z-10 flex items-center gap-3">
                            <div className="px-4 py-1.5 bg-black/40 border border-white/10 rounded-full flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                              <span className="text-[10px] font-black text-white/80 tracking-widest">
                                {t.activeEngine}
                              </span>
                            </div>
                          </div>

                          <div className="absolute top-6 right-6 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                            <button
                              onClick={() => setZoomImageUrl(selectedImage)}
                              className="w-10 h-10 bg-white/10 border border-white/10 rounded-lg text-white hover:bg-white/20 hover:scale-110 active:scale-90 flex items-center justify-center transition-all"
                            >
                              <Maximize2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = selectedImage!;
                                link.download = "design.png";
                                link.click();
                              }}
                              className="w-10 h-10 bg-white/10 border border-white/10 rounded-lg text-white hover:bg-white/20 hover:scale-110 active:scale-90 flex items-center justify-center transition-all"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>

                          {showSatisfactionPrompt &&
                            !isGeneratingImages &&
                            !isSubmittedForReality && (
                              <div className="absolute top-6 right-6 z-30 w-[300px] bg-white/90 rounded-2xl p-6 shadow-2xl border border-white/40 animate-fade-up">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="w-8 h-8 bg-red-50 rounded-full flex items-center justify-center">
                                    <MessageSquareHeart className="w-4 h-4 text-red-600" />
                                  </div>
                                  <h4 className="text-[13px] font-black text-black tracking-tight">
                                    {t.satisfactionTitle}
                                  </h4>
                                </div>
                                <p className="text-[11px] font-bold text-slate-500 leading-relaxed mb-6 italic">
                                  {t.satisfactionDesc}
                                </p>
                                <div className="space-y-2">
                                  <button
                                    onClick={handleTurnToReality}
                                    className="w-full py-3.5 bg-red-600 text-white rounded-lg text-[11px] font-black tracking-widest shadow-xl shadow-red-100 hover:bg-red-700 hover:scale-[1.02] active:scale-95 transition-all"
                                  >
                                    {t.satisfactionYes}
                                  </button>
                                  <button
                                    onClick={() =>
                                      setShowSatisfactionPrompt(false)
                                    }
                                    className="w-full py-3.5 bg-slate-100 text-slate-500 rounded-lg text-[11px] font-black tracking-widest hover:bg-slate-200 hover:scale-[1.02] active:scale-95 transition-all"
                                  >
                                    {t.satisfactionNo}
                                  </button>
                                </div>
                              </div>
                            )}

                          <div className="flex-1 flex items-center justify-center overflow-hidden p-8 lg:p-16">
                            <img
                              src={selectedImage}
                              onClick={() => setZoomImageUrl(selectedImage)}
                              className="max-w-full max-h-full object-contain drop-shadow-[0_48px_120px_rgba(0,0,0,0.8)] transition-all duration-1000 group-hover:scale-[1.02] cursor-zoom-in"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center p-12 space-y-10">
                          <div className="relative">
                            <div className="w-32 h-32 bg-white/5 rounded-3xl flex items-center justify-center border border-white/10">
                              <ImageIcon className="w-12 h-12 text-white/10" />
                            </div>
                            <Sparkles className="w-8 h-8 text-red-600 absolute -top-4 -right-4" />
                          </div>
                          <p className="text-[12px] font-black text-white/20 tracking-[0.3em] leading-relaxed max-w-[280px] mx-auto">
                            {t.noImages}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="h-[180px] shrink-0 bg-slate-900 border-t border-white/5 flex overflow-visible">
                      <div className="flex-1 border-r border-white/5 flex flex-col min-w-0">
                        <div className="h-10 px-5 border-b border-white/5 flex items-center justify-between bg-black/20 shrink-0 overflow-visible">
                          <div className="flex items-center gap-2.5">
                            <HistoryIcon className="w-3 h-3 text-white/20" />
                            <span className="text-[9px] font-black text-white/40 tracking-[0.2em]">
                              {t.assetsPool}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 relative z-[60] h-full overflow-visible">
                            {versionedImages.length > 0 && (
                              <div className="relative group/v-dropdown flex items-center h-full">
                                <button className="flex items-center gap-1 px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 hover:scale-105 active:scale-95 transition-all">
                                  <Layers2 className="w-2.5 h-2.5 text-red-500" />
                                  <span className="text-[9px] font-black text-white tracking-widest">
                                    V{viewVersionIndex + 1}
                                  </span>
                                  <ChevronDown className="w-2 h-2 text-white/40 group-hover/v-dropdown:rotate-180 transition-transform" />
                                </button>

                                <div className="absolute bottom-full right-0 pt-2 w-[110px] opacity-0 translate-y-2 pointer-events-none group-hover/v-dropdown:opacity-100 group-hover/v-dropdown:translate-y-0 group-hover/v-dropdown:pointer-events-auto transition-all z-[70]">
                                  <div className="bg-slate-900 border border-white/10 rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden">
                                    <div className="max-h-[180px] overflow-y-auto custom-scrollbar p-1.5 flex flex-col gap-1">
                                      {versionedImages.map((_, idx) => (
                                        <button
                                          key={idx}
                                          onClick={() =>
                                            handleSwitchVersion(idx)
                                          }
                                          className={`w-full py-2 rounded-lg text-[9px] font-black transition-all border border-transparent ${viewVersionIndex === idx ? "bg-red-600 text-white shadow-lg border-red-500" : "text-white/40 hover:bg-white/5 hover:text-white"}`}
                                        >
                                          Version V{idx + 1}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="h-2 w-2 bg-slate-900 absolute bottom-1 right-6 rotate-45 border-r border-b border-white/10 pointer-events-none"></div>
                                </div>
                              </div>
                            )}
                            <span className="text-[9px] font-black text-white/20 tracking-widest">
                              {currentViewImages.length} Units
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 flex min-w-0">
                          <div className="flex-1 overflow-x-auto custom-scrollbar flex items-center px-5 gap-3 scroll-smooth">
                            {currentViewImages.length > 0 ? (
                              currentViewImages.map((img, i) => (
                                <button
                                  key={`${viewVersionIndex}-${i}`}
                                  onClick={() => {
                                    setSelectedImage(img);
                                    setShowSatisfactionPrompt(false);
                                  }}
                                  className={`relative shrink-0 w-20 h-20 rounded-lg border-2 transition-all duration-500 overflow-hidden group/thumb ${selectedImage === img ? "border-red-600 scale-105 shadow-xl shadow-red-900/40 ring-4 ring-red-600/10" : "border-white/5 opacity-40 hover:opacity-100 hover:scale-105 hover:border-white/20"}`}
                                >
                                  <img
                                    src={img}
                                    className="w-full h-full object-cover"
                                  />
                                </button>
                              ))
                            ) : (
                              <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-white/5 rounded-xl opacity-20 my-3">
                                <Layers className="w-5 h-5 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-[260px] shrink-0 flex flex-col bg-black/40 p-5 space-y-2.5 border-l border-white/5">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] font-black text-white/40 tracking-widest">
                              {t.optTitle}
                            </span>
                            <div className="flex items-center gap-1">
                              <Zap className="w-2 h-2 text-yellow-500 fill-current" />
                              <span className="text-[9px] font-black text-yellow-500/60">
                                {t.costTag}
                              </span>
                            </div>
                          </div>
                          <div className="relative group">
                            <textarea
                              value={refinementPrompt}
                              onChange={(e) =>
                                setRefinementPrompt(e.target.value)
                              }
                              placeholder={t.refinePlaceholder}
                              className="w-full h-16 bg-white/5 border border-white/10 rounded-lg p-3 text-[11px] font-bold text-white outline-none focus:border-red-600/50 transition-all placeholder:text-white/10 resize-none custom-scrollbar shadow-inner"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => {
                              handleGenerateImages(refinementPrompt);
                              setRefinementPrompt("");
                            }}
                            disabled={isGeneratingImages}
                            className="w-full h-9 bg-white/5 hover:bg-white/10 border border-white/5 rounded-lg text-[10px] font-black text-white/80 tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-20 hover:scale-[1.02] active:scale-95"
                          >
                            {isGeneratingImages ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3 text-red-500" />
                            )}
                            {t.btnRegen}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Inspiration Detail Modal */}
      {selectedInspiration && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            onClick={() => setSelectedInspiration(null)}
          />
          <div className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row animate-in zoom-in duration-300">
            {/* Left: Image */}
            <div className="w-full md:w-1/2 aspect-square relative">
              <img
                src={selectedInspiration.image}
                alt={selectedInspiration[lang]}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent md:hidden" />
              <button
                onClick={() => setSelectedInspiration(null)}
                className="absolute top-4 left-4 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors md:hidden"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Right: Content */}
            <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center text-red-600">
                      {selectedInspiration.icon}
                    </div>
                    <span className="text-[10px] font-black text-red-600 tracking-wider">
                      Inspiration Case
                    </span>
                  </div>
                  <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
                    {selectedInspiration[lang]}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedInspiration(null)}
                  className="hidden md:flex w-10 h-10 bg-slate-100 rounded-full items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-8 flex-grow">
                {/* Description */}
                <div>
                  <p className="text-slate-600 leading-relaxed">
                    {selectedInspiration.details.description[lang]}
                  </p>
                </div>

                {/* Target Users */}
                <div>
                  <h4 className="text-[11px] font-black text-slate-400 tracking-wider mb-3">
                    Target Users
                  </h4>
                  <div className="flex items-start gap-3 text-slate-700">
                    <User className="w-5 h-5 text-red-500 mt-0.5" />
                    <p className="font-medium">
                      {selectedInspiration.details.targetUsers[lang]}
                    </p>
                  </div>
                </div>

                {/* Core Features */}
                <div>
                  <h4 className="text-[11px] font-black text-slate-400 tracking-wider mb-3">
                    Core Features
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedInspiration.details.features[lang].map(
                      (feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-sm font-bold text-slate-600 bg-slate-50 px-3 py-2 rounded-lg"
                        >
                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                          {feature}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <button
                  onClick={() => {
                    setInitialIdea(
                      selectedInspiration.details.description[lang],
                    );
                    setSelectedInspiration(null);
                  }}
                  className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-red-600 transition-all duration-300 group"
                >
                  {lang === "zh" ? "以此为灵感开启" : "Start with this concept"}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeationStep;
